/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React, { useContext, useEffect, useState } from 'react';
import { Form } from 'react-final-form';
import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';

import { Button } from '@blend/alchemy';

import { AlertBar, Modal } from 'blb-components/GrommetComponents';
import { useToasts } from 'blb-components/AuthenticatedApp/Toast';
import { createTeam } from 'blb-api/groups';
import { getUserProfile } from 'blb-api/users';
import { MeContext } from 'blb-contexts/Me';

import { BlendBridge } from 'blb-types/blendBridge';

import { InviteTeamMembers, MemberAssignment } from './InviteTeamMembers';
import { ShareSettings, ShareType } from './ShareSettings';
import TeamInfoForm from './TeamInfoForm';

const getErrorMessage = (error: Error) => `Error while trying to create a group: ${error.message}`;

const ModalStyles = {
  content: {
    height: 'fit-content',
    width: '500px',
  },
};

type DisplayStep = 'CREATE_TEAM' | 'SHARE_SETTINGS' | 'INVITE_MEMBERS';

export interface CreateTeamWithMembersFormValues {
  addressLineOne: string;
  addressLineTwo?: string;
  city: string;
  state: string;
  zipcode: string;
  broadcasterEmail: string;
  members: MemberAssignment[];
  name: string;
  shareType: ShareType;
}

const convertMembersDataForAPI = (members: MemberAssignment[]) =>
  members
    .filter(({ username }) => username)
    .map(member => {
      const { isAdmin, isBroadcaster, principalType, username } = member;

      return {
        principalType,
        isBroadcaster,
        username,
        role: isAdmin ? BlendBridge.API.GroupRoles.ADMIN : BlendBridge.API.GroupRoles.MEMBER,
      };
    });

interface TeamCreationValidationErrors {
  members?: string[];
  shareType?: string;
}
type CreateTeamStateMachineStep = Record<
  DisplayStep,
  {
    getComponent: (form: any, values: CreateTeamWithMembersFormValues) => JSX.Element;
    config: {
      modalTitle: string;
      submitLabel: string;
    };
    next: DisplayStep | null;
    previous: DisplayStep | null;
    validate?: (values: CreateTeamWithMembersFormValues) => TeamCreationValidationErrors;
  }
>;

interface CreateTeamModalProps {
  handleClose: () => Promise<void>;
  isOpen: boolean;
}

const CreateTeamModal: React.FC<CreateTeamModalProps> = ({ handleClose, isOpen }) => {
  const [error, setError] = useState<Error | undefined>();
  const [displayConfig, setDisplayConfig] = useState<{ modalTitle: string; submitLabel: string }>();
  const [displayStep, setDisplayStep] = useState<DisplayStep>('CREATE_TEAM');
  const [broadcasterEmail, setBroadcasterEmail] = useState<string>();
  const [userEmail, setUserEmail] = useState<string>();
  const me = useContext(MeContext)!;

  const stateMachine = React.useMemo<CreateTeamStateMachineStep>(
    () => ({
      CREATE_TEAM: {
        getComponent: (_form: any) => <TeamInfoForm />,
        config: {
          modalTitle: 'Create new team',
          submitLabel: 'Next',
        },
        next: 'SHARE_SETTINGS',
        previous: null,
      },
      SHARE_SETTINGS: {
        getComponent: (_form: any, values: CreateTeamWithMembersFormValues) => (
          <ShareSettings selectedShareType={values.shareType} />
        ),
        config: {
          modalTitle: 'Team sharing settings',
          submitLabel: 'Next',
        },
        next: 'INVITE_MEMBERS',
        previous: 'CREATE_TEAM',
        validate: (values: CreateTeamWithMembersFormValues) => {
          const errors: TeamCreationValidationErrors = {};
          if (!values.shareType) {
            errors.shareType = 'Please select the type of sharing for the team.';
          }
          return errors;
        },
      },
      INVITE_MEMBERS: {
        getComponent: (form: FormApi<any>) => (
          <InviteTeamMembers broadcasterEmail={broadcasterEmail} currentUserEmail={userEmail} form={form} />
        ),
        config: {
          modalTitle: 'Invite team members',
          submitLabel: 'Create & Invite',
        },
        next: null,
        previous: 'SHARE_SETTINGS',
        validate: (values: CreateTeamWithMembersFormValues) => {
          const errors: TeamCreationValidationErrors = {};
          if (!values.members.some(m => m.isAdmin)) {
            errors.members = ['Please select at least one member to be an admin'];
          }
          return errors;
        },
      },
    }),
    [broadcasterEmail, userEmail],
  );

  useEffect(() => {
    setDisplayConfig(stateMachine[displayStep].config);
  }, [displayStep]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      const { data: fetchedUserInfo } = await getUserProfile(me.principalId);
      setUserEmail(fetchedUserInfo.username);
    };

    // don't need to refetch logged in users email if we have it
    if (isOpen && !userEmail) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetchUserProfile();
    }
  }, [isOpen]);

  const Toaster = useToasts()!;

  const handleCloseModal = async () => {
    setError(undefined);
    setBroadcasterEmail(undefined);
    setDisplayStep('CREATE_TEAM');
    await handleClose();
  };

  const handleBack = () => {
    const { previous } = stateMachine[displayStep];
    // condition just for type checking
    if (previous) {
      setDisplayStep(previous);
    }
  };

  const onSubmit = async (formValues: CreateTeamWithMembersFormValues) => {
    setError(undefined);

    // for transitioning between ShareSettings and InviteMembers
    if (
      typeof formValues.broadcasterEmail !== 'undefined' &&
      formValues.shareType === 'CENTRALIZED' &&
      formValues.broadcasterEmail !== broadcasterEmail
    ) {
      setBroadcasterEmail(formValues.broadcasterEmail);
    } else if (formValues.shareType === 'OPEN' && !!broadcasterEmail) {
      setBroadcasterEmail(undefined);
      formValues.broadcasterEmail = '';
    }

    const { next } = stateMachine[displayStep];

    if (next) {
      setDisplayStep(next);
      return;
    }

    try {
      const { members, shareType: _unusedShareType, broadcasterEmail: _unusedEmail, ...teamCreationData } = formValues;
      const memberAssignments = convertMembersDataForAPI(members);
      await createTeam({ ...teamCreationData, assignments: memberAssignments });

      Toaster.addToast({
        message: `Team ${teamCreationData.name} successfully created`,
        theme: 'info',
      });
      await handleCloseModal();
    } catch (err) {
      setError(err as Error);
    }
  };

  if (!isOpen || !displayConfig) {
    return null;
  }

  return (
    <Form
      mutators={{
        ...arrayMutators,
      }}
      onSubmit={onSubmit}
      render={({ form, values, handleSubmit, submitting }) => {
        return (
          <Modal
            buttonComponents={
              <Button
                disabled={submitting}
                id="it-create-team-submit"
                label={displayConfig.submitLabel}
                onClick={handleSubmit}
                primary
              />
            }
            handleBack={displayStep !== 'CREATE_TEAM' ? handleBack : undefined}
            handleClose={handleCloseModal}
            id="create-team-modal"
            isOpen={isOpen}
            style={ModalStyles}
            title={displayConfig.modalTitle}
          >
            {error ? (
              <AlertBar
                boxProps={{ margin: { bottom: 'medium' } }}
                errorMessage={getErrorMessage(error)}
                id="it-create-group-error"
              />
            ) : null}
            {stateMachine[displayStep].getComponent(form, values)}
          </Modal>
        );
      }}
      validate={stateMachine[displayStep].validate}
    ></Form>
  );
};

export default CreateTeamModal;
